/**
 * @file src/multi/renderHost.js
 * @description The orbiter realm's render host. The compositor itself now lives in
 * `entangled-worlds-orbiters-shared/render` — Entangled Worlds needs the same one-canvas/one-renderer
 * host for its feed embeds, and it cannot import from this app. This module is the thin seam that
 * supplies the ORBITER-specific policy the shared module deliberately leaves to its host:
 *
 * - `devHandleKey`: the handle tooling reaches for to force a composited frame when rAF is
 *   throttled. Opt-in in the library, so a coexisting EW realm doesn't get it.
 * - performance mode: the realm follows this app's preference. A single orbiter owns its renderer and
 *   its loop, so it applies the mode itself; a voice in a collection or a room owns neither — pixel
 *   ratio, shadow map and frame rate are one per realm, and the compositor is the only thing that can
 *   set them without one voice deciding for its siblings. This is the seam where the app's preference
 *   reaches it, which is why the library takes a plain boolean and knows nothing about where it came
 *   from: Entangled Worlds uses the same compositor and never mentions the mode, so its realms keep
 *   the renderer and the cadence they always had.
 *
 * (The old `publishRendererGlobal` seam is gone with shared 3.0: every voice threads its renderer
 * explicitly — `orbitersApp` hands `worldController.renderer` to `WorldManagerExtended` — so no
 * loader reads a window global anymore.)
 */
import { createViewportCompositor as createSharedViewportCompositor } from 'entangled-worlds-orbiters-shared/render';
import {
  initPerformanceMode,
  isPerformanceModeEnabled,
  subscribePerformanceMode,
} from '../config/performanceMode.js';
import { createNoRenderHost } from './noRenderHost.js';

const DEV_HANDLE_KEY = '__orbitersViewportCompositor';

/**
 * The realm's own cell layout, for the boot that supplies none (`?multi=1`).
 *
 * The same square-ish arrangement and the same per-cell rules the compositor's built-in grid used,
 * lifted OUT of the hosts so it outlives them: the container is click-through so the gaps pass
 * through, and each cell takes its own pointer events so a voice's camera input and DOM hit-testing
 * land on the right tile.
 */
function createRealmCellSource() {
  let grid = null;

  function ensureGrid(total) {
    if (grid) return grid;
    const cols = Math.max(1, Math.ceil(Math.sqrt(total || 1)));
    const rows = Math.max(1, Math.ceil((total || 1) / cols));
    grid = document.createElement('div');
    grid.id = 'multi-orbiter-grid';
    grid.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:41',
      'display:grid',
      'gap:2px',
      'pointer-events:none',
      `grid-template-columns:repeat(${cols},1fr)`,
      `grid-template-rows:repeat(${rows},1fr)`,
    ].join(';');
    document.body.appendChild(grid);
    return grid;
  }

  return {
    acquireCell(index, total) {
      const cell = document.createElement('div');
      cell.className = 'multi-orbiter-cell';
      cell.dataset.voiceIndex = String(index);
      cell.style.cssText =
        'position:relative;overflow:hidden;min-width:0;min-height:0;pointer-events:auto;';
      ensureGrid(total).appendChild(cell);
      return cell;
    },
    /**
     * Take a cell back when its voice leaves. The compositor deliberately never removes a cell it
     * did not build, so without this a voice removed from a realm would leave an empty tile — and a
     * room that adds and removes voices would accumulate them.
     */
    releaseCell(cell) {
      cell?.remove();
    },
    /** Torn down with the REALM, never with a host — see `cellSource` below. */
    dispose() {
      grid?.remove();
      grid = null;
    },
  };
}

/** @type {typeof createSharedViewportCompositor} */
export function createViewportCompositor(opts = {}) {
  // Resolve the preference HERE, not just in the voices. A realm is built before any voice exists,
  // and until a voice boots nothing else has read the url or this device's stored value — so asking
  // for the preference at this point would otherwise always get the module's "off" default. The url
  // case would heal itself on the first voice (it resolves and notifies), but a device that simply
  // remembers the mode from last time never notifies, and the realm would keep full pixel ratio and
  // full frame rate while every voice in it drew the minimal scene: the savings this exists for,
  // silently absent, in exactly the case the mode was turned on for.
  //
  // Idempotent — a voice calling it again resolves to the same value.
  initPerformanceMode();

  // The cells belong to the REALM, never to whichever host is current. A host is replaced on every
  // switch and the outgoing one is disposed — and a host that owned the cell DOM would take the
  // voices' whole interfaces down with it, which is the one thing this mode is supposed to leave
  // standing. A collection supplies its own cells (React owns the stages); the `?multi=1` boot
  // supplies none, so the realm lays them out itself and keeps them across every swap.
  const cellSource = opts.cellSource ?? createRealmCellSource();
  /** Whether the cells above are the realm's own — i.e. this realm both builds and reclaims them. */
  const ownsCells = cellSource !== opts.cellSource;
  /**
   * The realm's voices, held HERE rather than only inside whichever host is current. Swapping hosts
   * means handing the same voices to the new one, and the outgoing host is being disposed — so it
   * cannot be the thing that remembers them.
   */
  const voices = new Map();
  let inner = null;
  let disposed = false;

  const buildCompositor = () =>
    createSharedViewportCompositor({
      ...opts,
      cellSource,
      devHandleKey: DEV_HANDLE_KEY,
      // Always false. The mode no longer asks the compositor to draw more cheaply — with the mode on
      // there is no compositor at all, so by the time this is built the answer is settled.
      performanceMode: false,
    });

  /**
   * Swap the realm between a real compositor and no host at all.
   *
   * This is the realm's switch, and it is the realm's alone: pixel ratio, the frame loop and the one
   * renderer are shared by every voice, so a voice reaching for them would be deciding for its
   * siblings. Each voice's own scene-profile seam handles its scene CONTENTS and leaves the renderer
   * to this — see `createSceneProfileController`.
   *
   * Order matters in both directions and for the same reason: a voice must never be pointed at a
   * renderer that is about to be destroyed, or at one that does not exist yet.
   *
   * Toggles are user gestures and arrive faster than a switch completes. Going off AWAITS the
   * voices, and a switch back on runs to completion inside that gap — so each swap takes a ticket
   * and a superseded one stops rather than finishing into a realm that has already moved on. Without
   * it, an off→on burst ends with the resumed first swap disposing the compositor the second one
   * just built: the preference says the 3D is on and the realm has no renderer and no loop at all.
   * The same guard the per-voice seam uses, for the same reason — see `createSceneProfileController`.
   */
  let generation = 0;
  async function swap(off) {
    if (disposed) return;
    const ticket = ++generation;
    if (off) {
      // AWAITED, and that is the whole point. Each voice releases its planet, its moons and its
      // skybox through the renderer — so the renderer has to still be alive while it happens. The
      // voices react to this same preference on their own too, but a subscription cannot promise to
      // finish before this line continues; awaiting them can. `apply` is idempotent, so whichever
      // starts first does the work and the other returns immediately.
      await Promise.all([...voices.values()].map((voice) => voice.sceneProfile?.apply(true)));
      if (disposed || ticket !== generation) return;
      // Now the voices let go of their reference, and only then is the renderer destroyed.
      voices.forEach(({ controller }) => controller?.setSceneProfile?.('off'));
      inner?.dispose?.();
      inner = createNoRenderHost({ cellSource });
      voices.forEach((voice, voiceId) => inner.addVoice({ voiceId, ...voice }));
      return;
    }
    inner?.dispose?.();
    inner = buildCompositor();
    voices.forEach((voice, voiceId) => inner.addVoice({ voiceId, ...voice }));
    // Built, re-registered, and only now handed out — so a voice's first drawn frame goes to a
    // renderer the compositor is already driving. The voices rebuild their own contents into it from
    // their own subscriptions, which run after this one.
    voices.forEach(({ controller }) =>
      controller?.setSceneProfile?.('full', { sharedRenderer: inner.renderer }),
    );
  }

  inner = isPerformanceModeEnabled() ? createNoRenderHost({ cellSource }) : buildCompositor();

  // The preference module's listener set outlives any one realm, so this has to come back off on
  // dispose — otherwise a closed room would leave a callback in there holding the whole compositor,
  // its renderer and every voice it was drawing.
  //
  // This subscription is registered before any voice of this realm exists, and listeners are notified
  // in the order they subscribed — so the renderer is in place before the voices react to the same
  // switch and start rebuilding their scenes into it.
  const unsubscribe = subscribePerformanceMode((enabled) => {
    void swap(enabled).catch((error) => console.warn('[renderHost] realm switch failed', error));
  });

  // A facade rather than the compositor itself, because the compositor is now a thing that gets
  // replaced. `renderer` and `canvas` are read fresh on every access for that reason: a caller that
  // captured either one at realm-boot would be holding the wrong object after the first toggle.
  return {
    get renderer() {
      return inner?.renderer ?? null;
    },
    get canvas() {
      return inner?.canvas ?? null;
    },
    createCell: (index, total) => inner?.createCell(index, total),
    addVoice: (voice) => {
      if (disposed || voice?.voiceId == null) return;
      voices.set(voice.voiceId, {
        cell: voice.cell,
        controller: voice.controller,
        // The voice's scene-profile seam, so the realm can sequence the switch for every voice at once.
        sceneProfile: voice.sceneProfile ?? null,
      });
      inner?.addVoice(voice);
    },
    removeVoice: (voiceId) => {
      const voice = voices.get(voiceId);
      voices.delete(voiceId);
      // The host goes first: if this voice was fullscreen the compositor has to bring the shared
      // canvas home before its cell is taken out from under it.
      inner?.removeVoice(voiceId);
      // A cell the realm built is a cell the realm reclaims. A collection's stages are the studio's
      // and are left exactly where they are — the layout re-fills the slot itself.
      if (ownsCells && voice?.cell) cellSource.releaseCell(voice.cell);
    },
    renderOnce: (...args) => inner?.renderOnce?.(...args),
    setPerformanceMode: (enabled) => swap(Boolean(enabled)),
    dispose: () => {
      disposed = true;
      unsubscribe();
      voices.clear();
      inner?.dispose?.();
      inner = null;
      // Only a layout the REALM built is a layout the realm removes; a collection's stages belong to
      // the studio that lent them.
      if (ownsCells) cellSource.dispose();
    },
  };
}
